import React, { useState, useEffect, useRef } from 'react';
import { getTherapistTimings, getTimingCodes } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { toast } from 'react-toastify';
import Loader from '../../../../Components/Loader/Loader';
import Echo from '../../../../Components/Sockect/Echo';
import EditIcon from '../../../../Components/Icons/EditIcon';
import WorkHoursPopup from './WorkHoursPopup';
import { checkActiveBookings } from '../../../../Utils/checkActiveBookings';
import styles from './WorkHours.module.css';

// Module-level cache for timing codes per outlet (persists across component re-renders)
let cachedTimingCodeMapsByOutlet = {};

const WorkHours = ({ therapistId, startDate }) => {
    const { selectedOutlet, loggedOutletID, selectedDate: homeSelectedDate, setHomeStaffList } = useGlobalContext();
    const outletId = selectedOutlet || loggedOutletID;
    const [timingsData, setTimingsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedEditDate, setSelectedEditDate] = useState(null);
    const [selectedShiftInfo, setSelectedShiftInfo] = useState(null);
    const [timingCodes, setTimingCodes] = useState(
        () => (outletId && cachedTimingCodeMapsByOutlet[outletId]) || {}
    );
    const skipNextEventFetchRef = useRef(false);
    const [checkingBookings, setCheckingBookings] = useState(false);

    // Format date as DD-MM-YYYY for API
    const formatDateForAPI = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Format time from HH:MM:SS to HH:MM (24-hour format)
    const formatTime = (timeString) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        return `${hours}:${minutes}`;
    };

    const formatShiftTimeWithExpansion = (shiftInfo) => {
        if (!shiftInfo?.checkin || !shiftInfo?.checkout) return '';
        const baseLabel = `${formatTime(shiftInfo.checkin)} - ${formatTime(shiftInfo.checkout)}`;
        return Number(shiftInfo?.is_schedule_calendar_expansion ?? shiftInfo?.is_calendar_expansion) === 1
            ? `${baseLabel} +1`
            : baseLabel;
    };

    const formatEventDateForAPI = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('-')) {
            const [first, second, third] = dateString.split('-');
            return first.length === 4 ? `${third}-${second}-${first}` : dateString;
        }
        return dateString;
    };

    const normalizeSocketSchedule = (schedule) => ({
        ...schedule,
        checkin: schedule.checkin || schedule.start_time,
        checkout: schedule.checkout || schedule.end_time,
        status: schedule.status || (Number(schedule.leave) === 1 ? 'OFF' : ''),
        remark: schedule.remark || '',
    });

    // Get week dates array
    const getWeekDates = () => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            dates.push(date);
        }
        return dates;
    };

    // Get day name
    const getDayName = (date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[date.getDay()];
    };

    // Fetch timing codes per outlet (refetch when outlet changes)
    useEffect(() => {
        if (!outletId) return;

        if (cachedTimingCodeMapsByOutlet[outletId]) {
            setTimingCodes(cachedTimingCodeMapsByOutlet[outletId]);
            return;
        }

        fetchTimingCodes(outletId);
    }, [outletId]);

    const fetchTimingCodes = async (outletIdParam) => {
        try {
            const response = await getTimingCodes({ 
                pagination: 0,
                outlet: outletIdParam
            });
            
            if (response?.data?.success) {
                const codes = response.data.data?.list?.timing_code || [];
                const codesMap = codes.reduce((acc, code) => {
                    acc[String(code.id)] = code.code;
                    return acc;
                }, {});
                
                cachedTimingCodeMapsByOutlet[outletIdParam] = codesMap;
                setTimingCodes(codesMap);
            }
        } catch (error) {
            console.error('Error fetching timing codes:', error);
        }
    };

    // Fetch timings data
    useEffect(() => {
        if (therapistId && (selectedOutlet || loggedOutletID)) {
            fetchTimings();
        }
    }, [therapistId, startDate, selectedOutlet, loggedOutletID]);

    useEffect(() => {
        if (!Echo || typeof Echo.channel !== "function") return;

        const channel = Echo.channel("outlet");
        const handleScheduleUpdated = (event) => {
            const outletId = selectedOutlet || loggedOutletID;
            const eventDates = Array.isArray(event?.dates) ? event.dates : [];
            const weekDates = getWeekDates();
            const matchesTherapist = String(event?.therapist_id) === String(therapistId);
            const matchesOutlet = !event?.outlet_id || String(event.outlet_id) === String(outletId);
            const matchesWeek = weekDates.some((date) => {
                const formatted = formatDateForAPI(date); // DD-MM-YYYY
                const [d, m, y] = formatted.split('-');
                const ymd = `${y}-${m}-${d}`;
                return eventDates.includes(formatted) || eventDates.includes(ymd);
            });

            if (therapistId && outletId && matchesTherapist && matchesOutlet && matchesWeek) {
                if (skipNextEventFetchRef.current) {
                    skipNextEventFetchRef.current = false;
                } else if (Array.isArray(event?.schedules)) {
                    setTimingsData((prev) => {
                        const therapistKey = String(event.therapist_id);
                        const next = { ...(prev || {}) };
                        const nextTherapistData = {
                            ...(next[therapistKey] || {}),
                        };

                        eventDates.forEach((eventDate) => {
                            const apiDate = formatEventDateForAPI(eventDate);
                            const schedulesForDate = event.schedules
                                .filter((schedule) => schedule.activity_date === eventDate || formatEventDateForAPI(schedule.activity_date) === apiDate)
                                .map(normalizeSocketSchedule);

                            nextTherapistData[apiDate] = schedulesForDate;
                        });

                        next[therapistKey] = nextTherapistData;
                        return next;
                    });
                } else {
                    fetchTimings();
                }
            }

            // Only sync Home staff list when outlet + event date match Home calendar selected date
            const matchesHomeOutlet = outletId && event?.outlet_id && String(event.outlet_id) === String(outletId);
            if (
                matchesHomeOutlet && event?.dates && event?.therapist_id && event?.therapist?.id && homeSelectedDate instanceof Date &&  !isNaN(homeSelectedDate.getTime())
            ) {
                const homeDateStr = formatDateForAPI(homeSelectedDate); // DD-MM-YYYY
                const [hd, hm, hy] = homeDateStr.split('-');
                const homeDateYmd = `${hy}-${hm}-${hd}`;
                const matchesHomeDate =
                    eventDates.includes(homeDateStr) || eventDates.includes(homeDateYmd);

                if (matchesHomeDate) {
                    setHomeStaffList((prev) => {
                        const safeList = Array.isArray(prev) ? prev : [];
                        const exists = safeList.some(
                            staff => String(staff.id) === String(event.therapist_id)
                        );
                        if (!exists && event.hide !== 1) {
                            return [event.therapist, ...safeList];
                        }
                        if (exists && event.hide === 1) {
                            return safeList.filter(
                                staff => String(staff.id) !== String(event.therapist_id)
                            );
                        }
                        return safeList;
                    });
                }
            }
        };

        channel.listen(".therapist.schedule.updated", handleScheduleUpdated);

        return () => {
            if (typeof channel.stopListening === "function") {
                channel.stopListening(".therapist.schedule.updated");
            }
        };
    }, [therapistId, startDate, selectedOutlet, loggedOutletID, homeSelectedDate, setHomeStaffList]);

    const fetchTimings = async () => {
        if (!therapistId) return;
        
        setLoading(true);
        setError(null);

        try {
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 6);

            const params = {
                user: therapistId,
                outlet: selectedOutlet || loggedOutletID,
                start_date: formatDateForAPI(startDate),
                end_date: formatDateForAPI(endDate),
                leave: 0,
            };

            const response = await getTherapistTimings(params);

            if (response?.success && response?.data?.list) {
                // API returns data filtered by therapistId, so we get only this therapist's data
                setTimingsData(response.data.list);
            } else {
                setError('Failed to load work hours');
                setTimingsData(null);
            }
        } catch (err) {
            console.error('Error fetching work hours:', err);
            setError('Error loading work hours');
            toast.error('Error loading work hours');
            setTimingsData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (date) => {
        if (checkingBookings) return;
        const shiftInfo = getShiftInfo(date);

        setCheckingBookings(true);
        const hasBookings = await checkActiveBookings({
            therapistId,
            outletId: selectedOutlet || loggedOutletID,
            date,
        });
        setCheckingBookings(false);

        setSelectedEditDate(date);
        setSelectedShiftInfo(
            shiftInfo
                ? { ...shiftInfo, has_current_date_booking: hasBookings }
                : { has_current_date_booking: hasBookings }
        );
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
        setSelectedEditDate(null);
        setSelectedShiftInfo(null);
    };

    const handleScheduleSuccess = () => {
        if (therapistId && (selectedOutlet || loggedOutletID)) {
            skipNextEventFetchRef.current = true;
            fetchTimings();
        }
    };

    // Get shift info for a specific date
    const getShiftInfo = (date) => {
        if (!timingsData || !therapistId) return null;

        const dateKey = formatDateForAPI(date);
        const therapistData = timingsData[String(therapistId)];

        if (!therapistData || !therapistData[dateKey] || therapistData[dateKey].length === 0) {
            return null;
        }

        const shift = therapistData[dateKey][0]; // Get first shift
        return shift;
    };

    const getTimingCodeLabel = (shiftInfo) => {
        if (shiftInfo?.timing_code) return shiftInfo.timing_code;
        const timingCodeId = shiftInfo?.timing_code_id;
        if (!timingCodeId) return 'Custom schedule';
        return timingCodes[String(timingCodeId)] || 'Custom schedule';
    };

    if (loading) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    if (!therapistId) {
        return (
            <div className={styles.workHoursContainer}>
                <div className={styles.emptyState}>
                    Please select a therapist to view work hours
                </div>
            </div>
        );
    }

    const weekDates = getWeekDates();

    return (
        <div className={styles.workHoursContainer}>
            <div
                className={`${styles.scheduleList} ${checkingBookings ? styles.scheduleListBusy : ''}`}
                aria-busy={checkingBookings}
            >
                {weekDates.map((date, index) => {
                    const shiftInfo = getShiftInfo(date);
                    const dayName = getDayName(date);
                    const dayDate = date.getDate();
                    const monthShort = date.toLocaleDateString('en-US', { month: 'short' });

                    return (
                        <div 
                            key={index} 
                            className={styles.scheduleRow}
                        >
                            <div 
                                className={styles.dateInfo}
                                onClick={() => handleEdit(date)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleEdit(date);
                                    }
                                }}
                                aria-label={`Edit shift for ${dayName}, ${dayDate} ${monthShort}`}
                            >
                                <span className={styles.dayName}>{dayName}</span>
                                <span className={styles.dateText}>{dayDate} {monthShort}</span>
                            </div>

                            <div 
                                className={styles.shiftInfo}
                                onClick={() => handleEdit(date)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleEdit(date);
                                    }
                                }}
                                aria-label={`Edit shift for ${dayName}, ${dayDate} ${monthShort}`}
                            >
                                {shiftInfo ? (
                                    shiftInfo.leave === 1 ? (
                                        <div className={styles.shiftContent}>
                                            <span className={styles.shiftCode}>OFF</span>
                                            <span className={styles.shiftTime}>
                                                {shiftInfo.status && shiftInfo.remark 
                                                    ? `${shiftInfo.status} - ${shiftInfo.remark}`
                                                    : shiftInfo.status 
                                                    ? shiftInfo.status
                                                    : shiftInfo.remark 
                                                    ? shiftInfo.remark
                                                    : ''
                                                }
                                            </span>
                                        </div>
                                    ) : (!shiftInfo.checkin && !shiftInfo.checkout) ? (
                                        <span className={styles.noShift}>No Shift</span>
                                    ) : (
                                        <div className={styles.shiftContent}>
                                            <span className={styles.shiftCode}>
                                                {getTimingCodeLabel(shiftInfo)}
                                            </span>
                                            <span className={styles.shiftTime}>
                                                {formatShiftTimeWithExpansion(shiftInfo)}
                                            </span>
                                        </div>
                                    )
                                ) : (
                                    <span className={styles.noShift}>No Shift</span>
                                )}
                                <div className={styles.editIconWrapper}>
                                    <EditIcon size={16} color="currentColor" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            <WorkHoursPopup
                isOpen={isPopupOpen}
                onClose={handleClosePopup}
                selectedDate={selectedEditDate}
                weekDates={weekDates}
                shiftInfo={selectedShiftInfo}
                therapistId={therapistId}
                onSuccess={handleScheduleSuccess}
                from='workhour'
            />
        </div>
    );
};

export default WorkHours;

